import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    StyledBasketBox,
    StyledFarmTitle,
    StyledGreenButton,
    StyledImage,
    StyledMediumTypography,
    StyledModalButtons,
    StyledNormalTypography,
    StyledRowBox
} from '../../theme/StyledElements';
import { useNavigate } from 'react-router-dom';
import { getImageKitUrlFrom } from '../../utils';
import { UserContext } from '../../providers/UserProvider';
import { Producteurs } from '../../models/Producteurs';
import { getProducer } from '../../services/cloudFirestore';
import { useMountedRef } from '../../hooks/useMountedRef';

export function ViewFarm(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const user = useContext(UserContext);
    const mounted = useMountedRef();
    const [producteurs, setProducteurs] = useState<Producteurs>({
        idProducteur: undefined,
        mail: undefined,
        nom: undefined,
        prenom: undefined,
        nomFerme: undefined,
        telephone: undefined,
        adresse: undefined,
        infoFerme: undefined,
        illustrationUrl: undefined,
        panierRecuperationMethode: undefined
    });

    useEffect(() => {
        if (user && user.idProducteur && mounted.current) {
            getProducer(user.idProducteur).then((updatedUser) => {
                if (updatedUser && mounted.current) {
                    setProducteurs({
                        idProducteur: updatedUser.idProducteur,
                        mail: updatedUser.mail,
                        nom: updatedUser.nom,
                        prenom: updatedUser.prenom,
                        nomFerme: updatedUser.nomFerme,
                        telephone: updatedUser.telephone,
                        adresse: updatedUser.adresse,
                        infoFerme: updatedUser.infoFerme,
                        illustrationUrl: updatedUser.illustrationUrl,
                        panierRecuperationMethode: updatedUser.panierRecuperationMethode
                    });
                }
            });
        }
    }, []);

    const getFarmDetails = (data?: string, text?: string): JSX.Element => {
        return (
            <StyledRowBox sx={{ maxWidth: '745px' }}>
                {text && (
                    <Typography variant="subtitle2" sx={{ marginRight: '3px' }}>
                        {text} {t('semicolon')}
                    </Typography>
                )}
                {data && (
                    <StyledNormalTypography
                        variant="body2"
                        color="#394A35"
                        sx={{ marginLeft: '3px', lineBreak: 'anywhere' }}>
                        {data}
                    </StyledNormalTypography>
                )}
            </StyledRowBox>
        );
    };

    return (
        <Box>
            {producteurs && (
                <StyledBasketBox>
                    <StyledNormalTypography variant="h2">{t('myFarm')}</StyledNormalTypography>
                    <StyledFarmTitle variant="body1">{t('generalInformation')}</StyledFarmTitle>
                    {getFarmDetails(producteurs.nom, t('lastName'))}
                    {getFarmDetails(producteurs.prenom, t('firstName'))}
                    {getFarmDetails(producteurs.nomFerme, t('nameOfFarm'))}
                    {getFarmDetails(producteurs.idProducteur, t('customerReference'))}
                    <StyledFarmTitle variant="body1">{t('contactInformation')}</StyledFarmTitle>
                    {getFarmDetails(producteurs.telephone, t('telephone'))}
                    {getFarmDetails(producteurs.mail, t('email'))}
                    <StyledFarmTitle variant="body1">{t('basketRecoveryAddress')}</StyledFarmTitle>
                    {getFarmDetails(producteurs.adresse)}
                    <StyledFarmTitle variant="body1">{t('methodToRetrieveBasket')}</StyledFarmTitle>
                    {getFarmDetails(producteurs.panierRecuperationMethode)}
                    <StyledFarmTitle variant="body1">{t('farmDescription')}</StyledFarmTitle>
                    <StyledNormalTypography
                        variant="subtitle2"
                        sx={{ marginRight: '3px', lineBreak: 'anywhere' }}>
                        {producteurs.infoFerme}
                    </StyledNormalTypography>
                    <StyledFarmTitle variant="body1">
                        {t('illustrativePictureOfFarm')}
                    </StyledFarmTitle>
                    {producteurs.illustrationUrl &&
                        typeof producteurs.illustrationUrl === 'string' && (
                            <StyledImage src={getImageKitUrlFrom(producteurs.illustrationUrl)} />
                        )}
                    <StyledModalButtons
                        sx={{ justifyContent: 'flex-end', margin: '20px 25px 20px 105px' }}>
                        <StyledGreenButton onClick={(): void => navigate('/modify-farm')}>
                            <StyledMediumTypography
                                variant="caption"
                                color="#FFFFFF"
                                sx={{ textTransform: 'uppercase' }}>
                                {t('modifyMyFarmInformation')}
                            </StyledMediumTypography>
                        </StyledGreenButton>
                    </StyledModalButtons>
                </StyledBasketBox>
            )}
        </Box>
    );
}
